import { Flex, Input, Text } from '@chakra-ui/react'
import React, { useState } from 'react'
import {
  formatPhoneNumberForBlocking,
  useCanBlockNumber,
} from '~/atoms/BlockedNumbersState'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { useBlockedNumberModalStyles } from '~/pages/Settings/componentStyles/BlockedNumberModal.styles'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

interface BlockNumberModalProps {
  isOpen: boolean
  onClose: () => void
  onBlockNumber: (phoneNumber: string) => void
  isLoading: boolean
}

export const BlockNumberModal = ({
  isOpen,
  onClose,
  onBlockNumber,
  isLoading,
}: BlockNumberModalProps) => {
  const [userInput, setUserInput] = useState('')

  const canBlockNumber = useCanBlockNumber(userInput)

  const styles = useBlockedNumberModalStyles()

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const lastChar = value.slice(-1)
    const isDeleting = userInput.length > value.length
    const isAddingSpecialChar = lastChar === ')' || lastChar === '-'

    // added logic to account for backspacing.
    if (isDeleting) {
      setUserInput(value)
    } else if (!isAddingSpecialChar) {
      const formattedValue = formatPhoneNumber(value)
      setUserInput(formattedValue)
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('Text')
    const formattedValue = formatPhoneNumber(pastedText)
    setUserInput(formattedValue)
    e.preventDefault()
  }

  const handleModalSubmit = () => {
    const cleanedNumber = formatPhoneNumberForBlocking(userInput)
    onBlockNumber(cleanedNumber)
    onClose()
    setUserInput('')
  }

  return (
    <MangoModal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setUserInput('')
      }}
      header="Block Number"
      size={Size.md}
      footer={
        <Flex sx={styles.footer}>
          <MangoButton
            variant={MangoButtonVariant.Default_100}
            onClick={() => setUserInput('')}
          >
            Clear
          </MangoButton>
          <MangoButton
            variant={MangoButtonVariant.Destructive_300}
            onClick={handleModalSubmit}
            isLoading={isLoading}
            isDisabled={!canBlockNumber}
          >
            Block Number
          </MangoButton>
        </Flex>
      }
    >
      <Flex sx={styles.body}>
        <Text>
          All calls, texts, and faxes from this number will be blocked.
        </Text>
        <Flex sx={styles.inputContainer}>
          <Input
            value={userInput}
            onChange={handleInputChange}
            placeholder="Enter Phone Number"
            type="tel"
            maxLength={14}
            onPaste={handlePaste}
          />
        </Flex>
      </Flex>
    </MangoModal>
  )
}
